// frontend/src/components/MiniBacktestPanel.jsx
import React, { useState } from "react";
import { useAuth } from "../AuthProvider";

const MiniBacktestPanel = ({ code, limitDays = 60 }) => {
  const { token } = useAuth();

  const [signals, setSignals] = useState({ BUY: true, WATCH: true });
  const [lookaheadDays, setLookaheadDays] = useState(10);
  const [entryMode, setEntryMode] = useState("close"); // close | buy_range | manual_offset
  const [manualOffset, setManualOffset] = useState(0); // % dari close

  const [slPct, setSlPct] = useState(5); // default 5%
  const [tp1Pct, setTp1Pct] = useState(""); // kosong = pakai default backend
  const [tp2Pct, setTp2Pct] = useState("");

  const [minScore, setMinScore] = useState(0); // 0–1

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [error, setError] = useState("");

  const handleSignalChange = (name) => {
    setSignals((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleRun = async () => {
    setError("");
    setSummary(null);
    setTrades([]);
    setLoading(true);
    try {
      const selectedSignals = Object.entries(signals)
        .filter(([, v]) => v)
        .map(([k]) => k);

      if (selectedSignals.length === 0) {
        setError("Pilih minimal satu jenis sinyal (BUY / WATCH).");
        setLoading(false);
        return;
      }

      const payload = {
        signals: selectedSignals,
        lookahead_days: Number(lookaheadDays),
        entry_mode: entryMode,
        manual_entry_offset_pct:
          entryMode === "manual_offset" ? Number(manualOffset || 0) : null,
        sl_pct: Number(slPct || 0),
        tp1_pct: tp1Pct === "" ? null : Number(tp1Pct),
        tp2_pct: tp2Pct === "" ? null : Number(tp2Pct),
        min_bandar_score: Number(minScore),
        limit_days: Number(limitDays),
      };

      const res = await fetch(`/api/stocks/${code}/mini-backtest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Gagal menjalankan mini backtest.");
      }

      const data = await res.json();
      setSummary(data.summary);
      setTrades(data.trades || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Terjadi error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-700 bg-slate-900/70 p-4 text-sm text-slate-100">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold">
          Mini Backtest — <span className="text-emerald-400">{code}</span>
        </h3>
        <button
          onClick={handleRun}
          disabled={loading}
          className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-emerald-400 disabled:opacity-60"
        >
          {loading ? "Running..." : "Run Mini Backtest"}
        </button>
      </div>

      {/* Form parameters */}
      <div className="grid gap-3 md:grid-cols-3">
        {/* Signals */}
        <div className="space-y-1">
          <div className="text-xs font-semibold text-slate-300">
            Signals
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={signals.BUY}
              onChange={() => handleSignalChange("BUY")}
            />
            BUY
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={signals.WATCH}
              onChange={() => handleSignalChange("WATCH")}
            />
            WATCH
          </label>

          <div className="mt-2 text-xs text-slate-400">
            Limit days: <span className="font-mono">{limitDays}</span>
          </div>
        </div>

        {/* Lookahead & entry */}
        <div className="space-y-1">
          <div className="text-xs font-semibold text-slate-300">
            Holding Window (Lookahead)
          </div>
          <select
            className="w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs"
            value={lookaheadDays}
            onChange={(e) => setLookaheadDays(e.target.value)}
          >
            <option value={5}>5 hari</option>
            <option value={10}>10 hari</option>
            <option value={15}>15 hari</option>
            <option value={20}>20 hari</option>
          </select>

          <div className="mt-2 text-xs font-semibold text-slate-300">
            Entry Price Mode
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="radio"
              value="close"
              checked={entryMode === "close"}
              onChange={(e) => setEntryMode(e.target.value)}
            />
            Entry at Close
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="radio"
              value="buy_range"
              checked={entryMode === "buy_range"}
              onChange={(e) => setEntryMode(e.target.value)}
            />
            Entry on Buy Range
          </label>
          <label className="mt-1 flex items-center gap-2 text-xs">
            <input
              type="radio"
              value="manual_offset"
              checked={entryMode === "manual_offset"}
              onChange={(e) => setEntryMode(e.target.value)}
            />
            Manual Offset (% dari close)
          </label>
          {entryMode === "manual_offset" && (
            <input
              type="number"
              step="0.1"
              className="mt-1 w-24 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs"
              value={manualOffset}
              onChange={(e) => setManualOffset(e.target.value)}
              placeholder="-2 = 2% di bawah"
            />
          )}
        </div>

        {/* TP/SL & min score */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-300">
            TP / SL Settings
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10">TP1%</span>
            <input
              type="number"
              step="0.1"
              className="w-20 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs"
              value={tp1Pct}
              onChange={(e) => setTp1Pct(e.target.value)}
              placeholder="auto"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10">TP2%</span>
            <input
              type="number"
              step="0.1"
              className="w-20 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs"
              value={tp2Pct}
              onChange={(e) => setTp2Pct(e.target.value)}
              placeholder="auto"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-10">SL%</span>
            <input
              type="number"
              step="0.1"
              className="w-20 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs"
              value={slPct}
              onChange={(e) => setSlPct(e.target.value)}
            />
          </div>

          <div className="mt-2 text-xs font-semibold text-slate-300">
            Min Bandar Score ({minScore.toFixed(2)})
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={minScore}
            onChange={(e) => setMinScore(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-3 rounded-md border border-red-600 bg-red-900/40 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="mt-4 grid gap-3 rounded-md border border-slate-700 bg-slate-800/60 p-3 text-xs md:grid-cols-4">
          <div>
            <div className="text-slate-400">Total Signals</div>
            <div className="font-mono text-sm">{summary.total_signals}</div>
          </div>
          <div>
            <div className="text-slate-400">Entries</div>
            <div className="font-mono text-sm">{summary.entries}</div>
          </div>
          <div>
            <div className="text-slate-400">TP2 / TP1 / SL</div>
            <div className="font-mono text-sm">
              {summary.tp2} / {summary.tp1} / {summary.sl}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Avg R:R</div>
            <div className="font-mono text-sm">
              {summary.avg_rr.toFixed(2)}R
            </div>
            <div className="mt-1 text-[11px] text-slate-400">
              Max loss streak: {summary.max_consecutive_loss}
            </div>
          </div>
        </div>
      )}

      {/* Trades table */}
      {trades && trades.length > 0 && (
        <div className="mt-3 max-h-64 overflow-auto rounded-md border border-slate-700">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="px-2 py-1 text-left">Date</th>
                <th className="px-2 py-1 text-left">Signal</th>
                <th className="px-2 py-1 text-right">Score</th>
                <th className="px-2 py-1 text-right">Entry</th>
                <th className="px-2 py-1 text-right">Exit</th>
                <th className="px-2 py-1 text-left">Outcome</th>
                <th className="px-2 py-1 text-right">R</th>
                <th className="px-2 py-1 text-right">Days</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((t, idx) => (
                <tr
                  key={idx}
                  className={
                    idx % 2 === 0 ? "bg-slate-900" : "bg-slate-900/70"
                  }
                >
                  <td className="px-2 py-1">
                    {t.date}
                  </td>
                  <td className="px-2 py-1">{t.signal}</td>
                  <td className="px-2 py-1 text-right">
                    {t.score.toFixed(2)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {t.entry_price != null ? t.entry_price.toFixed(2) : "-"}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {t.exit_price != null ? t.exit_price.toFixed(2) : "-"}
                  </td>
                  <td className="px-2 py-1">{t.outcome}</td>
                  <td className="px-2 py-1 text-right">
                    {t.rr != null ? t.rr.toFixed(2) : "-"}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {t.holding_days != null ? t.holding_days : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary && trades.length === 0 && !loading && (
        <div className="mt-3 text-xs text-slate-400">
          Tidak ada trade yang memenuhi kriteria filter.
        </div>
      )}
    </div>
  );
};

export default MiniBacktestPanel;
